/**
 * User Management Page
 * 
 * Admin page for viewing and managing users.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Filter, ChevronLeft, ChevronRight,
  Edit, Trash2, Shield, User, Crown, Star, Sparkles,
  MoreVertical, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { AppShell, GlassPanel } from '@/components/layout';
import { Button, Input, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { Navigate, Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

interface UserItem {
  id: string;
  email: string;
  name: string | null;
  role: string;
  tier: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  session_count: number;
}

const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const styles = {
    basic: 'bg-gray-500/20 text-gray-300 border-gray-500/50',
    standard: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    pro: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    elite: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  };
  
  const icons = {
    basic: <User size={12} />,
    standard: <Star size={12} />,
    pro: <Crown size={12} />,
    elite: <Sparkles size={12} />,
  };
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border capitalize',
      styles[tier as keyof typeof styles] || styles.basic
    )}>
      {icons[tier as keyof typeof icons]}
      {tier}
    </span>
  );
};

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span className={cn(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
    role === 'admin' 
      ? 'bg-red-500/20 text-red-300 border border-red-500/50' 
      : 'bg-navy-600 text-gray-400'
  )}>
    {role === 'admin' && <Shield size={12} />}
    {role}
  </span>
);

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const limit = 20;

  // Redirect non-admins
  if (currentUser?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = {
        skip: page * limit,
        limit,
      };
      
      if (search) params.search = search;
      if (tierFilter) params.tier = tierFilter;
      
      const res = await api.get('/admin/users', { params });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, tierFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(0);
      fetchUsers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const handleUpdateUser = async (userId: string, update: any) => {
    try {
      await api.patch(`/admin/users/${userId}`, update);
      fetchUsers();
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AppShell>
      <div className="p-8 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ChevronLeft size={16} />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-display font-bold text-white">User Management</h1>
              <p className="text-gray-400">{total} total users</p>
            </div>
          </div>
          
          <Button variant="ghost" onClick={fetchUsers} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <GlassPanel className="mb-6 p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name..."
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <select
                value={tierFilter || ''}
                onChange={(e) => setTierFilter(e.target.value || null)}
                className="bg-navy-700 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="pro">Pro</option>
                <option value="elite">Elite</option>
              </select>
            </div>
          </div>
        </GlassPanel>

        {/* Users Table */}
        <GlassPanel className="overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">User</th>
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Role</th>
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Tier</th>
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Status</th>
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Sessions</th>
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Last Login</th>
                <th className="text-right px-6 py-4 text-gray-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-6 py-4">
                      <Skeleton className="h-8 w-full" />
                    </td>
                  </tr>
                ))
              ) : (
                users.map((user) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{user.name || '—'}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-6 py-4">
                      <TierBadge tier={user.tier} />
                    </td>
                    <td className="px-6 py-4">
                      {user.is_active ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle size={14} />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-sm">
                          <XCircle size={14} />
                          Suspended
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {user.session_count}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {user.last_login_at 
                        ? new Date(user.last_login_at).toLocaleDateString()
                        : 'Never'
                      }
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingUser(user)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit size={14} />
                        </Button>
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-gray-400 hover:text-red-400"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
            <p className="text-gray-400 text-sm">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </p>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft size={16} />
                Previous
              </Button>
              
              <span className="text-gray-400 text-sm px-4">
                Page {page + 1} of {totalPages}
              </span>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </GlassPanel>

        {/* Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-navy-800 border border-white/10 rounded-xl p-6 w-full max-w-md"
            >
              <h2 className="text-xl font-semibold text-white mb-4">Edit User</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Email</label>
                  <p className="text-white">{editingUser.email}</p>
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Tier</label>
                  <select
                    value={editingUser.tier}
                    onChange={(e) => setEditingUser({ ...editingUser, tier: e.target.value })}
                    className="w-full bg-navy-700 border border-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="pro">Pro</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full bg-navy-700 border border-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Status</label>
                  <select
                    value={editingUser.is_active ? 'active' : 'suspended'}
                    onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.value === 'active' })}
                    className="w-full bg-navy-700 border border-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setEditingUser(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => handleUpdateUser(editingUser.id, {
                    tier: editingUser.tier,
                    role: editingUser.role,
                    is_active: editingUser.is_active,
                  })}
                >
                  Save Changes
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default UserManagement;

